/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CertSelfSignedBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the certificates module.
----------------------------------------------------------------*/


import { Eye } from 'lucide-react';
import { Lock } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../hooks/useI18n';
import { CertificateFormData, KEY_SIZE_OPTIONS, VALIDITY_OPTIONS } from '../types';

interface Props {
    formData: CertificateFormData;
    isEditMode: boolean;
    validating: boolean;
    onChange: (field: keyof CertificateFormData, value: CertificateFormData[keyof CertificateFormData]) => void;
    onPreview: () => void;
}

export function CertSelfSignedBox({ formData, isEditMode, validating, onChange, onPreview }: Props) {
    const { t } = useI18n();
    return (
        <FormBox title={t('certificates.sections.selfSigned')} icon={<Lock className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                <Label htmlFor="keySize" className="text-left">{t('certificates.fields.keySize')}</Label>
                <Select
                    value={formData.keySize.toString()}
                    onValueChange={(v) => onChange('keySize', parseInt(v))}
                    options={KEY_SIZE_OPTIONS.map(opt => ({ value: opt.value.toString(), label: opt.label }))}
                />
                <Label htmlFor="validityDays" className="text-left">{t('certificates.fields.validityDays')}</Label>
                <Select
                    value={formData.validityDays.toString()}
                    onValueChange={(v) => onChange('validityDays', parseInt(v))}
                    options={VALIDITY_OPTIONS.map(opt => ({ value: opt.value.toString(), label: opt.label }))}
                />
                {!isEditMode && (
                    <>
                        <div />
                        <div className="col-span-3 flex justify-start">
                            <Button variant="outline" onClick={onPreview} disabled={validating || !formData.commonName}>
                                <Eye className="w-4 h-4 mr-2" />
                                {t('certificates.actions.preview')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </FormBox>
    );
}
